// Advanced Graphs — curated interview problem with multiple worked approaches.
// Same shape as dsaProblems/graphs.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer using the (now weight-aware) graph rendering.

const advancedGraphProblems = [
  {
    id: 'network-delay-time',
    title: 'Network Delay Time',
    difficulty: 'Medium',
    leetcode: { number: 743, url: 'https://leetcode.com/problems/network-delay-time/' },
    statement:
      'A signal is sent from node k through a directed, weighted network of n nodes (times[i] = [u, v, w] means ' +
      'a signal from u reaches v in w time). Return the minimum time for ALL n nodes to receive the signal, or ' +
      '-1 if some node is unreachable.',
    example: {
      input: 'times = [[2,1,1],[2,3,1],[3,4,1]], n = 4, k = 2',
      output: '2',
      note: 'Node 1 and 3 receive the signal at time 1; node 4 (via node 3) receives it at time 2 — the last, and so the answer.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Explore Every Path)',
        why:
          "DFS out from k, trying every possible sequence of edges, tracking the time each node is first (or " +
          "more cheaply) reached. Correct, but the number of distinct paths through the network can grow " +
          "exponentially, and many of them redundantly re-explore the same nodes.",
        steps: [
          'DFS from k, tracking cumulative delay along the current path.',
          'Whenever a node is reached, compare this delay to the best recorded for it so far, and update if better.',
          'Continue exploring — a longer path might still turn out to beat a currently-recorded distance via a different route.',
          'Once all reachable nodes have a finalized minimum, the answer is the maximum value among them (the last node to receive the signal).',
        ],
        code:
`int networkDelayTimeBruteForce(int[][] times, int n, int k) {
    Map<Integer, List<int[]>> adj = new HashMap<>();
    for (int[] t : times) adj.computeIfAbsent(t[0], x -> new ArrayList<>()).add(new int[]{t[1], t[2]});

    int[] best = new int[n + 1];
    Arrays.fill(best, Integer.MAX_VALUE);
    dfs(k, 0, adj, best);

    int maxTime = 0;
    for (int node = 1; node <= n; node++) {
        if (best[node] == Integer.MAX_VALUE) return -1;
        maxTime = Math.max(maxTime, best[node]);
    }
    return maxTime;
}

private void dfs(int node, int time, Map<Integer, List<int[]>> adj, int[] best) {
    if (time >= best[node]) return;   // already reached this node at least as fast
    best[node] = time;
    for (int[] edge : adj.getOrDefault(node, List.of())) {
        dfs(edge[0], time + edge[1], adj, best);
    }
}`,
        complexity: { time: 'Exponential in the worst case (many overlapping paths)', space: 'O(V) — recursion depth' },
        whenToUse: "Only useful to confirm the problem's meaning out loud — Dijkstra's is specifically built for exactly this \"cheapest time to reach every node from one source\" question.",
      },
      {
        name: 'Approach 2 — Optimized: Dijkstra\'s Algorithm',
        why:
          "This is precisely what Dijkstra's algorithm computes: the shortest (cheapest) time from a single " +
          "source to every other node, using a priority queue to always expand whichever reachable node is " +
          "currently cheapest to reach. The answer is simply the LARGEST of the resulting distances — the last " +
          "node the signal reaches.",
        steps: [
          'Build a weighted adjacency list from the times array, and run Dijkstra\'s algorithm from source k.',
          'A min-heap always pops the node with the smallest known delay next, relaxing its outgoing edges.',
          'After Dijkstra finishes, check every node\'s final distance — if any is still infinity, it\'s unreachable, so return -1.',
          'Otherwise, the answer is the maximum distance across all nodes — the time the LAST node receives the signal.',
        ],
        visual: {
          graph: { label: 'network', nodes: [1, 2, 3, 4], edges: [[2, 1, 1], [2, 3, 1], [3, 4, 1]], directed: true },
          varNames: [],
          steps: [
            { highlights: { graph: [2] }, note: 'Start at k=2, distance 0. Push its edges: 2→1 (1), 2→3 (1).' },
            { highlights: { graph: [1, 3] }, note: 'Pop the smallest: both 1 and 3 are at distance 1 (tie). Process both: dist[1]=1 (no outgoing edges), dist[3]=1 — push 3→4 (1).' },
            { highlights: { graph: [4] }, note: 'Pop 4: dist[4] = 1 + 1 = 2. No more nodes to process. Final distances: dist[1]=1, dist[2]=0, dist[3]=1, dist[4]=2. Maximum: 2 — the answer.' },
          ],
        },
        code:
`int networkDelayTime(int[][] times, int n, int k) {
    Map<Integer, List<int[]>> adj = new HashMap<>();
    for (int[] t : times) adj.computeIfAbsent(t[0], x -> new ArrayList<>()).add(new int[]{t[1], t[2]});

    int[] dist = new int[n + 1];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[k] = 0;
    PriorityQueue<int[]> pq = new PriorityQueue<>((a, b) -> a[1] - b[1]);   // {node, time}
    pq.add(new int[] { k, 0 });

    while (!pq.isEmpty()) {
        int[] top = pq.poll();
        int node = top[0], time = top[1];
        if (time > dist[node]) continue;   // stale entry

        for (int[] edge : adj.getOrDefault(node, List.of())) {
            int neighbor = edge[0], weight = edge[1];
            if (dist[node] + weight < dist[neighbor]) {
                dist[neighbor] = dist[node] + weight;
                pq.add(new int[] { neighbor, dist[neighbor] });
            }
        }
    }

    int maxTime = 0;
    for (int node = 1; node <= n; node++) {
        if (dist[node] == Integer.MAX_VALUE) return -1;
        maxTime = Math.max(maxTime, dist[node]);
    }
    return maxTime;
}`,
        complexity: { time: 'O((V + E) log V)', space: 'O(V + E)' },
        interviewNote: "Try this exact algorithm in the \"Dijkstra's Shortest Path\" mode of this pattern's interactive playground, on any weighted graph you build.",
        whenToUse: "This is the textbook application of Dijkstra's algorithm — single-source shortest paths in a graph with non-negative weights, with the twist of reporting the WORST (maximum) of all the resulting distances rather than a specific target's distance.",
      },
    ],
  },
];

export default advancedGraphProblems;
